
import React from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { ScheduleAlert } from '../types';
import { useLanguage } from '@/contexts/LanguageContext';

interface AlertCardProps {
  alert: ScheduleAlert;
  onResolve?: (alertId: string) => Promise<void> | void;
}

// Function to determine alert color based on type
const getAlertTypeColor = (alertType: string) => {
  switch (alertType) {
    case 'INSUFFICIENT_HOURS':
    case 'insufficient_hours':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'UNASSIGNED_SCHEDULE':
    case 'unassigned_schedule':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'night_morning_shift':
      return 'bg-purple-100 text-purple-800 border-purple-300';
    case 'insufficient_rest':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'visibility_status':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    case 'missing_template_shifts':
      return 'bg-indigo-100 text-indigo-800 border-indigo-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

// Function to get human-readable alert type name
const getAlertTypeName = (alertType: string, t: any) => {
  switch (alertType) {
    case 'INSUFFICIENT_HOURS':
    case 'insufficient_hours':
      return t('insufficientHours') || 'Insufficient Hours';
    case 'UNASSIGNED_SCHEDULE':
    case 'unassigned_schedule':
      return t('unassignedSchedule') || 'Unassigned Schedule';
    case 'night_morning_shift':
      return t('nightMorningShift') || 'Night-Morning Shift';
    case 'insufficient_rest':
      return t('insufficientRest') || 'Insufficient Rest';
    case 'visibility_status':
      return t('visibilityStatus') || 'Visibility Status';
    case 'missing_template_shifts':
      return t('missingTemplateShifts') || 'Missing Template Shifts';
    default:
      return alertType;
  }
};

export const AlertCard: React.FC<AlertCardProps> = ({ alert, onResolve }) => {
  const { t } = useLanguage();
  
  const handleResolve = () => {
    if (onResolve) {
      onResolve(alert.id);
    }
  };
  
  const alertTypeColor = getAlertTypeColor(alert.alert_type);
  const alertTypeName = getAlertTypeName(alert.alert_type, t);
  
  return (
    <Card className={`w-full ${alert.is_resolved ? 'bg-muted' : ''}`}>
      <CardContent className="pt-4">
        <div className="flex justify-between items-start mb-2">
          <div>
            <Badge variant="outline" className={`mb-2 ${alert.is_resolved ? 'bg-muted-foreground/20' : alertTypeColor}`}>
              {alertTypeName}
            </Badge>
            {alert.is_resolved && (
              <Badge variant="outline" className="ml-2 bg-green-100 text-green-800 border-green-300">
                {t('resolved') || 'Resolved'}
              </Badge>
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            {format(new Date(alert.created_at), 'MMM d, yyyy')}
          </span>
        </div>
        
        <h3 className="text-lg font-semibold mb-1">{alert.team_name || ''}</h3>
        <p className="text-sm text-muted-foreground mb-2">
          {t('weekStarting') || 'Week starting'}: {format(new Date(alert.week_start_date), 'MMM d, yyyy')}
        </p>
        
        <div className="mt-3 text-sm">
          {alert.alert_message || alert.content}
        </div>
        
        {alert.resolved_by && (
          <div className="mt-3 text-xs text-muted-foreground">
            {t('resolvedBy') || 'Resolved by'}: {alert.resolved_by_name || alert.resolved_by}
          </div>
        )}
      </CardContent>
      
      {!alert.is_resolved && onResolve && (
        <CardFooter className="pt-0 pb-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleResolve}
            className="ml-auto"
          >
            {t('markAsResolved') || 'Mark as Resolved'}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};
